(function () {
    'use strict';

    function parseYouTubeUrl(src, baseUrl) {
        try {
            const url = new URL(src, baseUrl);
            const validProtocol =
                url.protocol === 'https:' ||
                url.protocol === 'http:';
            const validHost =
                url.hostname === 'youtube.com' ||
                url.hostname === 'www.youtube.com' ||
                url.hostname === 'youtube-nocookie.com' ||
                url.hostname === 'www.youtube-nocookie.com';

            if (!validProtocol || !validHost) return null;

            url.protocol = 'https:';
            return url;
        } catch (error) {
            return null;
        }
    }

    function parseYouTubeEmbed(src, baseUrl) {
        const url = parseYouTubeUrl(src, baseUrl);
        if (!url) return null;

        const match = url.pathname.match(/^\/embed\/([A-Za-z0-9_-]+)\/?$/);
        if (!match) return null;

        return {
            videoId: match[1],
            url: url.href
        };
    }

    function buildAutoplayUrl(src, baseUrl) {
        const parsed = parseYouTubeEmbed(src, baseUrl);
        if (!parsed) return '';

        const url = new URL(parsed.url);
        url.searchParams.set('autoplay', '1');
        return url.href;
    }

    function normalizeEmbedDimensions(width, height) {
        const parsedWidth = Number.parseFloat(width);
        const parsedHeight = Number.parseFloat(height);

        return {
            width: parsedWidth > 0 ? parsedWidth : 560,
            height: parsedHeight > 0 ? parsedHeight : 315
        };
    }

    function accessiblePlayLabel(title) {
        const normalizedTitle = (title || '').trim();
        return normalizedTitle
            ? `Reproduzir: ${normalizedTitle}`
            : 'Reproduzir vídeo do YouTube';
    }

    function thumbnailUrls(videoId) {
        return {
            primary: `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
            fallback: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
        };
    }

    function applyEmbedDimensions(element, dimensions) {
        element.style.setProperty(
            '--deferred-youtube-max-width',
            `${dimensions.width}px`
        );
        element.style.setProperty(
            '--deferred-youtube-aspect-ratio',
            `${dimensions.width} / ${dimensions.height}`
        );
    }

    function createFallbackLink(src) {
        const link = document.createElement('a');
        const parsedUrl = parseYouTubeUrl(src, document.baseURI);
        const safeUrl = parsedUrl
            ? parsedUrl.href
            : 'https://www.youtube.com/';

        link.className = 'deferred-youtube-fallback';
        link.href = safeUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = 'Assistir vídeo no YouTube';
        return link;
    }

    function ensureAutoplayPermission(iframe) {
        const currentAllow = iframe.getAttribute('allow') || '';
        const permissions = currentAllow
            .split(';')
            .map(permission => permission.trim())
            .filter(Boolean);
        const hasAutoplay = permissions.some(permission => {
            return permission.split(/\s+/)[0] === 'autoplay';
        });

        if (hasAutoplay) return;

        permissions.push('autoplay');
        iframe.setAttribute('allow', permissions.join('; '));
    }

    function createActivatedIframe(originalIframe, baseUrl) {
        const iframe = originalIframe.cloneNode(true);
        const activatedSrc = buildAutoplayUrl(
            originalIframe.getAttribute('src'),
            baseUrl
        );

        if (!activatedSrc) return null;

        iframe.src = activatedSrc;
        iframe.loading = 'eager';
        iframe.classList.add('deferred-youtube-frame');
        ensureAutoplayPermission(iframe);
        return iframe;
    }

    function createTrigger(originalIframe, parsed, dimensions) {
        const title = originalIframe.getAttribute('title');
        const thumbnails = thumbnailUrls(parsed.videoId);
        const button = document.createElement('button');
        const image = document.createElement('img');
        const play = document.createElement('span');
        const svgNamespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(svgNamespace, 'svg');
        const path = document.createElementNS(svgNamespace, 'path');

        button.type = 'button';
        button.className = 'deferred-youtube-trigger';
        button.setAttribute('aria-label', accessiblePlayLabel(title));
        applyEmbedDimensions(button, dimensions);

        image.className = 'deferred-youtube-thumbnail';
        image.src = thumbnails.primary;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.alt = '';
        image.setAttribute('aria-hidden', 'true');
        image.addEventListener('error', function fallbackThumbnail() {
            image.src = thumbnails.fallback;
        }, { once: true });

        play.className = 'deferred-youtube-play';
        play.setAttribute('aria-hidden', 'true');

        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('focusable', 'false');
        svg.setAttribute('aria-hidden', 'true');
        path.setAttribute('d', 'M8 5v14l11-7z');
        svg.append(path);
        play.append(svg);

        button.append(image, play);
        button.addEventListener('click', function activateVideo() {
            const iframe = createActivatedIframe(
                originalIframe,
                document.baseURI
            );

            if (!iframe) {
                button.replaceWith(
                    createFallbackLink(
                        originalIframe.getAttribute('src') || ''
                    )
                );
                return;
            }

            applyEmbedDimensions(iframe, dimensions);
            button.replaceWith(iframe);
        }, { once: true });

        return button;
    }

    if (!customElements.get('deferred-youtube-embed')) {
        customElements.define(
            'deferred-youtube-embed',
            class DeferredYouTubeEmbed extends HTMLElement {
                connectedCallback() {
                    if (this.dataset.initialized === 'true') return;

                    const template = this.querySelector(
                        'template[data-deferred-youtube-template]'
                    );
                    const content = this.querySelector(
                        '[data-deferred-youtube-content]'
                    );

                    if (!template || !content) return;

                    this.dataset.initialized = 'true';

                    const fragment = template.content.cloneNode(true);
                    const iframes = fragment.querySelectorAll('iframe[src]');

                    iframes.forEach(function replaceIframe(originalIframe) {
                        const src =
                            originalIframe.getAttribute('src') || '';
                        const youtubeUrl = parseYouTubeUrl(
                            src,
                            document.baseURI
                        );

                        if (!youtubeUrl) return;

                        const parsed = parseYouTubeEmbed(
                            src,
                            document.baseURI
                        );

                        if (!parsed) {
                            originalIframe.replaceWith(
                                createFallbackLink(src)
                            );
                            return;
                        }

                        const dimensions = normalizeEmbedDimensions(
                            originalIframe.getAttribute('width'),
                            originalIframe.getAttribute('height')
                        );
                        originalIframe.replaceWith(
                            createTrigger(
                                originalIframe,
                                parsed,
                                dimensions
                            )
                        );
                    });

                    content.append(fragment);
                }
            }
        );
    }
}());
